<template>
  <div>
    <!-- Tela de seleção de pacientes -->
    <div
      v-if="!usuarioSelecionado"
      fluid
    >
      <v-row justify="center">
        <v-col
          cols="12"
          md="10"
          lg="8"
        >
          <v-card
            variant="outlined"
            class="modern-card selection-card"
            min-width="800"
          >
            <v-card-text class="pa-0">
              <div class="selection-header primary white--text">
                <span class="material-icons">groups</span>
                Selecione um Paciente
              </div>
            </v-card-text>

            <v-card-text class="pa-4">
              <v-text-field
                v-model="search"
                variant="outlined"
                density="compact"
                label="Buscar paciente por nome, ID OS ou empresa"
                prepend-inner-icon="search"
                hide-details
                clearable
                class="search-field"
              />

              <v-progress-linear
                v-if="loadingUsuarios"
                indeterminate
                color="primary"
                class="my-4"
              />

              <div
                v-if="!loadingUsuarios && usuariosFiltrados.length === 0"
                class="empty-state"
              >
                <span class="material-icons empty-icon">search_off</span>
                <div class="empty-text">
                  Nenhum paciente encontrado
                </div>
              </div>

              <v-list 
                v-if="!loadingUsuarios && usuariosFiltrados.length > 0" 
                class="patient-list"
                lines="three"
              >
                <v-list-item
                  v-for="usuario in usuariosFiltrados"
                  :key="usuario.idexamesos"
                  class="patient-item"
                  :class="{ 'selected-user': usuarioSelecionado && usuarioSelecionado.idexamesos === usuario.idexamesos }"
                  variant="outlined"
                  @click="selecionarUsuario(usuario)"
                >
                  <template #prepend>
                    <v-avatar
                      color="primary"
                      size="50"
                      class="user-avatar"
                    >
                      <span class="white--text text-subtitle-1 font-weight-bold">
                        {{ getIniciais(usuario.paciente) }}
                      </span>
                    </v-avatar>
                  </template>

                  <v-list-item-title class="text-h6 primary--text user-name">
                    {{ usuario.paciente }}
                  </v-list-item-title>
                  
                  <v-list-item-subtitle class="user-status">
                    <span class="material-icons icon-sm">pause_circle</span>
                    <v-chip 
                      :color="usuario.status === 'Aguardando' ? 'warning' : 'primary'" 
                      variant="flat"
                      size="small"
                      class="status-chip"
                    >
                      {{ usuario.status }}
                    </v-chip>
                  </v-list-item-subtitle>
                  
                  <v-list-item-subtitle class="user-details">
                    <div class="detail-item">
                      <span class="material-icons icon-sm">business</span>
                      Empresa: {{ usuario.empresa_cliente }}
                    </div>
                    <div class="detail-item">
                      <span class="material-icons icon-sm">event</span>
                      Data: {{ formatarData(usuario.datalanca) }}
                    </div>
                  </v-list-item-subtitle>

                  <template #append>
                    <span class="material-icons chevron-icon">chevron_right</span>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Conteúdo principal após seleção -->
    <div
      v-else
      class="main-content"
    >
      <v-container
        fluid
        class="pa-4"
      >
        <v-row>
          <!-- Menu lateral esquerdo -->
          <v-col
            cols="12"
            md="2"
            class="sidebar-col"
          >
            <v-card
              variant="outlined"
              class="modern-card sidebar-card"
            >
              <div class="sidebar-content">
                <!-- Botão Voltar -->
                <v-card
                  variant="outlined"
                  class="back-card"
                >
                  <v-btn
                    color="secondary"
                    variant="text"
                    block
                    class="back-btn"
                    @click="voltarParaLista"
                  >
                    <span class="material-icons">arrow_back</span>
                    Voltar
                  </v-btn>
                </v-card>

                <v-divider class="my-3" />

                <!-- Menu de Navegação -->
                <v-list
                  class="nav-list"
                  density="compact"
                >
                  <v-list-item-group 
                    v-model="selectedNavItem" 
                    active-class="nav-item-active"
                    color="primary"
                    mandatory
                  >
                    <v-list-item
                      v-for="(item, i) in navItems"
                      :key="i"
                      :value="item.id"
                      :disabled="item.disabled"
                      class="nav-item modern-tab"
                    >
                      <template #prepend>
                        <div class="icon-badge-container">
                          <span class="material-icons tab-icon">{{ item.icon.replace('mdi-', '') }}</span>
                          <v-badge
                            v-if="item.badge && item.badge > 0"
                            :content="item.badge"
                            color="error"
                            class="icon-badge"
                            floating
                          />
                        </div>
                      </template>

                      <v-list-item-title class="nav-item-text tab-label">
                        {{ item.text }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>

                <v-divider class="my-3" />

                <!-- Ações Rápidas -->
                <v-list
                  class="action-list"
                  density="compact"
                >
                  <v-list-item 
                    class="action-item success-item"
                    @click="liberarBloquearAso(true)"
                  >
                    <template #prepend>
                      <span
                        class="material-icons"
                        style="color: var(--v-theme-success)"
                      >check_circle</span>
                    </template>
                    <v-list-item-title class="action-text">
                      LIBERAR ASO
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item 
                    class="action-item error-item"
                    @click="liberarBloquearAso(false)"
                  >
                    <template #prepend>
                      <span
                        class="material-icons"
                        style="color: var(--v-theme-error)"
                      >block</span>
                    </template>
                    <v-list-item-title class="action-text">
                      BLOQUEAR ASO
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
            </v-card>
          </v-col>

          <!-- Conteúdo principal central -->
          <v-col
            cols="12"
            md="6"
            class="content-col"
          >
            <!-- Resumo Clínico -->
            <v-card 
              v-show="selectedNavItem === 0" 
              variant="outlined"
              class="modern-card content-card"
            >
              <v-toolbar density="comfortable">
                <v-toolbar-title
                  class="text-h6"
                  style="color: rgb(var(--v-theme-on-surface));"
                >
                  <span class="material-icons">description</span>
                  Resumo Clínico
                </v-toolbar-title>
              </v-toolbar>
              
              <v-card-text class="content-body">
                <v-skeleton-loader 
                  v-if="loadResumo" 
                  type="article, list-item-three-line, list-item-three-line"
                  class="skeleton-item"
                />
                
                <div v-else>
                  <div
                    v-if="resumoIntro"
                    class="resumo-content"
                    v-html="resumoIntro"
                  />
                  <div
                    v-if="resumoProntuarios"
                    class="resumo-content"
                    v-html="resumoProntuarios"
                  />
                  
                  <div
                    v-if="!resumoIntro && !resumoProntuarios"
                    class="empty-state"
                  >
                    <span class="material-icons empty-icon">description</span>
                    <div class="empty-text">
                      Nenhum resumo clínico disponível
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Prontuário -->
            <v-card 
              v-show="selectedNavItem === 1" 
              variant="outlined"
              class="modern-card content-card"
            >
              <!-- <fichaPaciente 
                :idExamesOS="usuarioSelecionado.idexamesos" 
                @voltar="voltarLaudo" 
              /> -->
            </v-card>

            <!-- Solicitações -->
            <v-card 
              v-show="selectedNavItem === 2" 
              variant="outlined"
              class="modern-card content-card"
            >
              <v-card variant="outlined">
                <v-sheet
                  min-height="70vh"
                  rounded="lg"
                >
                  <!-- Lista de Solicitações -->
                  <div
                    v-if="!isAddSolicitacao"
                    class="solicitacoes-container"
                  >
                    <v-container>
                      <v-row>
                        <!-- Solicitações Regulares -->
                        <v-col 
                          v-for="(solicitacao, n) in solicitacoesCards" 
                          :key="`regular-${n}`"
                          cols="12"
                          md="4"
                        >
                          <v-card
                            variant="outlined"
                            class="solicitacao-card modern-card"
                          >
                            <v-card-text class="solicitacao-content">
                              <ol class="exame-list">
                                <li 
                                  v-for="(item, index) in solicitacao.items" 
                                  :key="index"
                                  class="exame-item"
                                >
                                  {{ item.nome }}
                                </li>
                              </ol>
                            </v-card-text>

                            <v-card-actions class="solicitacao-actions">
                              <v-spacer />
                              <v-btn
                                icon
                                color="secondary"
                                size="small"
                                class="action-btn"
                                @click="editSolicitacao(n, false)"
                              >
                                <span class="material-icons">edit</span>
                              </v-btn>
                              <v-btn
                                icon
                                color="error"
                                size="small"
                                class="action-btn"
                                @click="solicitacoesCards.splice(n, 1)"
                              >
                                <span class="material-icons">delete</span>
                              </v-btn>
                            </v-card-actions>

                            <v-divider v-if="solicitacao.isObs" />

                            <v-textarea
                              v-if="solicitacao.isObs"
                              v-model="solicitacao.observacao"
                              variant="outlined"
                              density="compact"
                              label="Observação"
                              auto-grow
                              readonly
                              class="observacao-field"
                            />
                          </v-card>
                        </v-col>

                        <!-- Solicitações Laboratório -->
                        <v-col 
                          v-for="(solicitacao, n) in solicitacoesLaboratorioCards" 
                          :key="`lab-${n}`"
                          cols="12"
                          md="4"
                        >
                          <v-card
                            variant="outlined"
                            class="solicitacao-card modern-card lab-card"
                          >
                            <v-card-text class="solicitacao-content">
                              <ol class="exame-list">
                                <li 
                                  v-for="(item, index) in solicitacao.items" 
                                  :key="index"
                                  class="exame-item"
                                >
                                  {{ item.nome }}
                                </li>
                              </ol>
                            </v-card-text>

                            <v-card-actions class="solicitacao-actions">
                              <v-spacer />
                              
                              <v-tooltip location="top">
                                <template #activator="{ props }">
                                  <v-btn
                                    v-bind="props"
                                    icon
                                    color="primary"
                                    size="small"
                                    class="action-btn"
                                    @click="imprimirSolicitacao(solicitacao.items, solicitacao.observacao, 'LABORATORIO', solicitacao.data)"
                                  >
                                    <span class="material-icons">print</span>
                                  </v-btn>
                                </template>
                                <span>Abrir opções de impressão</span>
                              </v-tooltip>

                              <v-tooltip location="top">
                                <template #activator="{ props }">
                                  <v-btn
                                    v-bind="props"
                                    icon
                                    color="secondary"
                                    size="small"
                                    class="action-btn"
                                    @click="editSolicitacao(n, true)"
                                  >
                                    <span class="material-icons">edit</span>
                                  </v-btn>
                                </template>
                                <span>Editar solicitação</span>
                              </v-tooltip>

                              <v-tooltip location="top">
                                <template #activator="{ props }">
                                  <v-btn
                                    v-bind="props"
                                    icon
                                    color="error"
                                    size="small"
                                    class="action-btn"
                                    @click="solicitacoesLaboratorioCards.splice(n, 1)"
                                  >
                                    <span class="material-icons">delete</span>
                                  </v-btn>
                                </template>
                                <span>Excluir solicitação</span>
                              </v-tooltip>
                            </v-card-actions>

                            <v-divider v-if="solicitacao.isObs" />

                            <v-textarea
                              v-if="solicitacao.isObs"
                              v-model="solicitacao.observacao"
                              variant="outlined"
                              density="compact"
                              label="Observação"
                              auto-grow
                              readonly
                              class="observacao-field"
                            />
                          </v-card>
                        </v-col>

                        <!-- Card para Nova Solicitação -->
                        <v-col
                          cols="12"
                          md="4"
                        >
                          <v-card 
                            variant="outlined"
                            class="nova-solicitacao-card modern-card"
                            @click="isAddSolicitacao = true"
                          >
                            <v-card-text class="nova-solicitacao-content">
                              <div class="add-icon-container">
                                <span
                                  class="material-icons tab-icon"
                                  style="color: var(--v-theme-primary)"
                                >add_circle</span>
                                <div class="add-text">
                                  Nova Solicitação
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-container>
                  </div>

                  <!-- Formulário de Solicitação -->
                  <div
                    v-else
                    class="solicitacao-form-container"
                  >
                    <solicitacao 
                      :m-exames="examesEdit"
                      :is-lab-aux="isLab"
                      :paciente="usuarioSelecionado"
                      :is-edit-solicitacao="isEditSolicitacao"
                      @cancelar="resetarFormularioEdicao"
                      @solicitacoesLabCard="adicionarSolicitacaoLaboratorio"
                      @solicitacoesCard="adcionarSolicitacao"
                      @editarSolicitacaoLabCard="editarSolicitacaoLaboratorio"
                      @editarSolicitacaoCard="editarSolicitacao"
                    />
                  </div>
                </v-sheet>
              </v-card>
            </v-card>
          </v-col>

          <!-- Painel lateral direito com histórico -->
          <v-col
            cols="12"
            md="4"
            class="sidebar-col"
          >
            <v-card
              variant="outlined"
              class="modern-card historico-card"
            >
              <v-sheet rounded="lg">
                <v-card
                  variant="flat"
                  class="historico-header"
                >
                  <v-card-title class="text-h6 grey--text">
                    <span class="material-icons">history</span>
                    Histórico
                  </v-card-title>
                  <v-divider class="mt-2" />
                </v-card>

                <!-- Lista de exames -->
                <v-card-text class="historico-content">
                  <v-list
                    class="patient-list"
                    lines="three"
                  >
                    <v-list-item
                      v-for="(exame, index) in examesPaciente"
                      :key="index"
                      class="patient-item exame-item-card"
                    >
                      <v-list-item-title class="text-body-1 font-weight-bold primary--text">
                        <span class="material-icons icon-sm">event</span>
                        {{ formatarDataExame(exame.data) }}
                      </v-list-item-title>
                      
                      <v-list-item-subtitle class="text-caption mt-1">
                        <span class="material-icons icon-sm">business</span>
                        {{ exame.prestador }}
                      </v-list-item-subtitle>

                      <v-list-item-subtitle class="mt-1">
                        <v-chip 
                          :color="exame.tipo === 'Alterado' ? 'error' : 'success'" 
                          variant="flat"
                          size="x-small"
                          class="status-chip"
                        >
                          {{ exame.tipo }}
                        </v-chip>
                      </v-list-item-subtitle>

                      <div
                        class="text-body-2 mt-2"
                        v-html="exame.conteudo"
                      />

                      <v-divider
                        v-if="index < examesPaciente.length - 1"
                        class="mt-2"
                      />
                    </v-list-item>

                    <div
                      v-if="examesPaciente.length === 0"
                      class="empty-state"
                    >
                      <span class="material-icons empty-icon">info</span>
                      <div class="empty-text">
                        Nenhum exame encontrado
                      </div>
                    </div>
                  </v-list>
                </v-card-text>
              </v-sheet>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Floating Action Button para voltar -->
      <v-fab-transition>
        <v-btn
          color="primary"
          fab
          size="small"
          class="refresh-fab"
          @click="voltarParaLista"
        >
          <span class="material-icons">arrow_back</span>
        </v-btn>
      </v-fab-transition>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import medicoService from '../../services/medico.service'
// import fichaPaciente from './fichaPaciente.vue'
import solicitacao from './solicitacao.vue'
import { EventBus } from '../../main'

export default {
  components: {
    //fichaPaciente,
    solicitacao,
  },
  props: {
    tempoAtd: {
      type: String,
      default: '00:00:00'
    },
    consultasPaciente: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const store = useStore()
    
    // Estados reativos
    const usuarioSelecionado = ref(null)
    const usuarios = ref([])
    const search = ref('')
    const loadingUsuarios = ref(false)
    const selectedNavItem = ref(0)
    
    // Estados de solicitações
    const solicitacoesLaboratorioCards = ref([])
    const solicitacoesCards = ref([])
    const isAddSolicitacao = ref(false)
    const isEditSolicitacao = ref(false)
    const isLab = ref(false)
    const examesEdit = ref([])
    const indexToEdit = ref(null)
    const indexToEditLab = ref(null)
    
    // Estados de exames e histórico
    const examesPaciente = ref([])
    const loadResumo = ref(false)
    const resumoProntuarios = ref('')
    const resumoIntro = ref('')
    
    // Estados de UI
    const dialogAlerta = ref(false)
    const mensagemAlerta = ref('')
    
    // Itens de navegação
    const navItems = ref([
      {
        text: 'Resumo Clínico',
        icon: 'description',
        id: 0,
        badge: 0,
        disabled: false,
        color: '#00BCD4'
      },
      {
        text: 'Prontuário',
        icon: 'note_add',
        badge: 0,
        id: 1,
        disabled: false,
        color: '#1E88E5'
      },
      {
        text: 'Solicitação',
        icon: 'pending_actions',
        id: 2,
        badge: 0,
        disabled: false,
        color: '#7B68EE'
      }
    ])

    // Computed
    const usuariosFiltrados = computed(() => {
      if (!search.value) return usuarios.value

      const searchTerm = search.value.toLowerCase()
      return usuarios.value.filter(usuario =>
        usuario.paciente.toLowerCase().includes(searchTerm) ||
        (usuario.idos && usuario.idos.toString().includes(searchTerm)) ||
        (usuario.idexamesos && usuario.idexamesos.toString().includes(searchTerm)) ||
        (usuario.empresa_cliente && usuario.empresa_cliente.toLowerCase().includes(searchTerm))
      )
    })

    const user = computed(() => store.getters.usuario)

    // Métodos (mantidos os mesmos do código original)
    const extrairIdsExames = (solicitacoesCards, solicitacoesLaboratoriaisCards) => {
      const todosIds = []
      const extrairIdsDoArray = (array) => {
        if (array && array.length > 0) {
          array.forEach(solicitacao => {
            if (solicitacao.items && solicitacao.items.length > 0) {
              solicitacao.items.forEach(item => {
                if (item.idExame) {
                  todosIds.push(item.idExame)
                }
              })
            }
          })
        }
      }
      extrairIdsDoArray(solicitacoesCards)
      extrairIdsDoArray(solicitacoesLaboratoriaisCards)
      return todosIds
    }

    const liberarBloquearAso = async (status) => {
      if (!status) {
        const examesIds = extrairIdsExames(solicitacoesCards.value, solicitacoesLaboratorioCards.value)
        if (examesIds.length > 0) {
          await medicoService.medico.lancarPreOrdem(usuarioSelecionado.value.idexamesos, user.value.id, examesIds)
        }
      }
      
      try {
        const response = await medicoService.medico.liberarBloquearAso(
          usuarioSelecionado.value.idexamesos, 
          user.value.id, 
          status
        )
        if (response.status === 200) {
          mostrarAlerta('ASO atualizado com sucesso.')
          voltarParaLista()
        }
      } catch (error) {
        console.error('Erro ao atualizar ASO:', error)
        mostrarAlerta('Erro ao atualizar ASO.')
      }
    }

    const resetarFormularioEdicao = () => {
      isAddSolicitacao.value = false
      isEditSolicitacao.value = false
      examesEdit.value = []
      indexToEdit.value = null
      indexToEditLab.value = null
    }

    const carregarUsuarios = async () => {
      loadingUsuarios.value = true
      try {
        const response = await medicoService.medico.getReavaliacao(user.value.id)
        if (response.data && response.data.dados) {
          usuarios.value = response.data.dados.map(paciente => ({
            idos: paciente.idos,
            idexamesos: paciente.idexamesos,
            paciente: paciente.paciente,
            status: paciente.status,
            datalanca: paciente.datalanca,
            empresa_cliente: paciente.empresa_cliente,
            cpf: paciente.cpf || '',
            dataNascimento: paciente.dataNascimento || ''
          }))
        }
      } catch (error) {
        console.error('Erro ao carregar pacientes:', error)
        mostrarAlerta('Erro ao carregar lista de pacientes')
      } finally {
        loadingUsuarios.value = false
      }
    }

    const adcionarSolicitacao = (data) => {
      isAddSolicitacao.value = false
      if (data.isLaboratorio) {
        solicitacoesLaboratorioCards.value.push(data)
      } else {
        solicitacoesCards.value.push(data)
      }
    }

    const adicionarSolicitacaoLaboratorio = (data) => {
      isAddSolicitacao.value = false
      solicitacoesLaboratorioCards.value.push(data)
    }

    const editarSolicitacao = (data) => {
      solicitacoesCards.value[indexToEdit.value] = data
      resetarFormularioEdicao()
    }

    const editarSolicitacaoLaboratorio = (data) => {
      solicitacoesLaboratorioCards.value[indexToEditLab.value] = data
      resetarFormularioEdicao()
    }

    const editSolicitacao = (index, isLab) => {
      isLab.value = isLab
      isAddSolicitacao.value = true
      isEditSolicitacao.value = true

      if (isLab) {
        indexToEditLab.value = index
        examesEdit.value = [...solicitacoesLaboratorioCards.value[index].items]
      } else {
        indexToEdit.value = index
        examesEdit.value = [...solicitacoesCards.value[index].items]
      }
    }

    const selecionarUsuario = (usuario) => {
      usuarioSelecionado.value = usuario
      carregarDadosUsuario(usuario.idexamesos)
      carregarHistorio(usuario.idos)
      EventBus.$emit('showSideBar', false)
      EventBus.$emit('paciente-selecionado', usuario)
    }

    const carregarHistorio = async (idos) => {
      try {
        const response = await medicoService.medico.getLaudosOcupacional(idos)
        if (response.status === 200) {
          examesPaciente.value = response.data.dados || []
        }
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }

    const voltarParaLista = () => {
      EventBus.$emit('showSideBar', true)
      EventBus.$emit('item-exit', false)
      usuarioSelecionado.value = null
      search.value = ''
      resumoProntuarios.value = ''
      examesPaciente.value = []
    }

    const carregarDadosUsuario = async (idexamesos) => {
      try {
        retornarResumoProntuario(idexamesos)
        retornarInformacoesPacienteOs(usuarioSelecionado.value.idos)
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error)
        mostrarAlerta('Erro ao carregar dados do paciente')
      }
    }

    const retornarInformacoesPacienteOs = async (idOrdemServico) => {
      try {
        const response = await medicoService.medico.retornarInformacoesPacienteOs(idOrdemServico)
        if (response.status === 200) {
          const dadosPaciente = {
            ...response.data,
            nome: usuarioSelecionado.value.paciente
          }
          EventBus.$emit('paciente-selecionado', dadosPaciente)
        }
      } catch (error) {
        console.error('Erro ao buscar informações do paciente:', error)
      }
    }

    const getIniciais = (nome) => {
      if (!nome) return '??'
      return nome.split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    }

    const formatarData = (data) => {
      if (!data) return 'Data não informada'
      return new Date(data).toLocaleDateString('pt-BR')
    }

    const formatarDataExame = (data) => {
      if (!data) return 'Data não informada'
      return data.split(' ')[0]
    }

    const mostrarAlerta = (mensagem) => {
      mensagemAlerta.value = mensagem
      dialogAlerta.value = true
    }

    const retornarResumoProntuario = async (idos) => {
      loadResumo.value = true
      try {
        const response = await medicoService.medico.getResumoClinicoOcupacional(idos)
        if (response.status === 200) {
          resumoIntro.value = response.data.intro_html || ''
          resumoProntuarios.value = response.data.instrucoes_html || ''
        }
      } catch (error) {
        console.error('Erro ao carregar resumo:', error)
      } finally {
        loadResumo.value = false
      }
    }

    const voltarLaudo = () => {
      // Implementação específica se necessário
    }

    const imprimirSolicitacao = (items, observacao, tipo, data) => {
      // Implementação da impressão
      console.log('Imprimindo solicitação:', { items, observacao, tipo, data })
    }

    // Lifecycle
    onMounted(() => {
      carregarUsuarios()
    })

    // Watchers
    watch(selectedNavItem, (newValue) => {
      if (newValue !== null) {
        EventBus.$emit('item-selecionado', newValue)
      }
    })

    return {
      // Estados
      usuarioSelecionado,
      usuarios,
      search,
      loadingUsuarios,
      selectedNavItem,
      solicitacoesLaboratorioCards,
      solicitacoesCards,
      isAddSolicitacao,
      isEditSolicitacao,
      isLab,
      examesEdit,
      examesPaciente,
      loadResumo,
      resumoProntuarios,
      resumoIntro,
      dialogAlerta,
      mensagemAlerta,
      navItems,

      // Computed
      usuariosFiltrados,
      user,

      // Métodos
      liberarBloquearAso,
      resetarFormularioEdicao,
      carregarUsuarios,
      adcionarSolicitacao,
      adicionarSolicitacaoLaboratorio,
      editarSolicitacao,
      editarSolicitacaoLaboratorio,
      editSolicitacao,
      selecionarUsuario,
      carregarHistorio,
      voltarParaLista,
      carregarDadosUsuario,
      getIniciais,
      formatarData,
      formatarDataExame,
      mostrarAlerta,
      retornarResumoProntuario,
      voltarLaudo,
      imprimirSolicitacao
    }
  }
}
</script>

<style scoped>
/* Estilos padronizados baseados no primeiro componente */

.selection-card {
  border-radius: 16px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  position: relative;
  min-width: 800px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1); /* Adiciona borda ao card */
  margin-top: 16px; /* Espaço do topo */
}

/* Header de seleção */
.selection-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px; /* Altura menor */
  font-size: 18px;
  font-weight: 600;
  background: rgb(var(--v-theme-surface)); /* Fundo sólido */
  color: rgb(var(--v-theme-primary)) !important; /* Cor do texto primary */
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.1); /* Separador visual */
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.selection-header .material-icons {
  font-size: 24px;
  margin-right: 8px;
  color: rgb(var(--v-theme-primary)) !important; /* Cor do ícone primary */
}

/* Campos de busca */
.search-field {
  margin-bottom: 16px;
  border-radius: 12px;
}

.search-field :deep(.v-input__slot) {
  border-radius: 12px !important;
  background: white;
}

/* Listas de pacientes */
.patient-list {
  background: transparent;
  padding: 0;
}

.patient-item {
  margin: 0 0 8px 0;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
  background: white;
}

.patient-item:hover {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.04);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selected-user {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
}

.user-avatar {
  font-weight: 600;
}

.user-name {
  font-weight: 600;
  margin-bottom: 4px;
}

.user-status {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.user-details {
  margin-top: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  font-size: 0.875rem;
}

.status-chip {
  font-weight: 600;
  margin-left: 4px;
}

.icon-sm {
  font-size: 16px;
  margin-right: 6px;
}

.chevron-icon {
  transition: transform 0.3s ease;
  color: rgb(var(--v-theme-primary));
}

.patient-item:hover .chevron-icon {
  transform: translateX(4px);
}

/* Estados vazios */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.4;
  color: rgb(var(--v-theme-disabled));
}

.empty-text {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  font-weight: 500;
}

/* Sidebar e navegação */
.sidebar-card {
  height: 100%;
  border-radius: 16px;
  margin-top: 16px; /* Espaçamento superior */
}

.sidebar-content {
  padding: 16px;
}

.back-card {
  border-radius: 12px;
}

.back-btn {
  font-weight: 600;
  border-radius: 8px;
}

.nav-list {
  background: transparent;
}

.nav-item {
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  padding: 8px 16px; /* Altura menor */
  color: rgb(var(--v-theme-on-surface)); /* Cor do texto padrão */
}

.nav-item-active {
  background: rgba(var(--v-theme-primary), 0.1) !important; /* Fundo mais sutil */
  color: rgb(var(--v-theme-primary)) !important; /* Cor do texto primary */
  box-shadow: 0 1px 4px rgba(var(--v-theme-primary), 0.08); /* Sombra sutil */
  /* transform: translateY(-2px); Removido para simplificar o estilo */
}

.nav-item-text {
  font-weight: 600;
  font-size: 14px;
}

/* Ícones e badges */
.icon-badge-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.tab-icon {
  font-size: 20px;
  opacity: 0.9;
}

.icon-badge {
  position: absolute;
  top: -6px;
  right: -6px;
}

.icon-badge :deep(.v-badge__badge) {
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border: 2px solid white;
}

/* Ações */
.action-list {
  background: transparent;
}

.action-item {
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  padding: 12px 16px;
}

.action-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.success-item:hover {
  background: rgba(var(--v-theme-success), 0.1);
}

.error-item:hover {
  background: rgba(var(--v-theme-error), 0.1);
}

.action-text {
  font-weight: 600;
  font-size: 13px;
}

/* Conteúdo principal */
.content-card {
  height: 100%;
  border-radius: 16px;
  background-color: white; /* Garante fundo branco */
}

.content-body {
  padding: 20px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.resumo-content {
  line-height: 1.6;
  color: rgb(var(--v-theme-on-surface)) !important; /* Garante texto escuro */
}

.resumo-content :deep(p),
.resumo-content :deep(h1),
.resumo-content :deep(h2),
.resumo-content :deep(h3),
.resumo-content :deep(h4),
.resumo-content :deep(h5),
.resumo-content :deep(h6),
.resumo-content :deep(span) {
  color: rgb(var(--v-theme-on-surface)) !important; /* Garante texto escuro para elementos dentro do v-html */
}

.resumo-content :deep(p) {
  margin-bottom: 12px;
}

.resumo-content :deep(ul),
.resumo-content :deep(ol) {
  margin-bottom: 12px;
  padding-left: 20px;
}

.resumo-content :deep(li) {
  margin-bottom: 4px;
}

/* Solicitações */
.solicitacoes-container {
  min-height: 400px;
  padding: 16px;
}

.solicitacao-card {
  border-radius: 12px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  height: 100%;
  border: 2px solid transparent;
}

.solicitacao-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: rgb(var(--v-theme-primary), 0.2);
}

.lab-card {
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.solicitacao-content {
  padding: 16px;
  min-height: 120px;
}

.exame-list {
  padding-left: 16px;
  margin: 0;
}

.exame-item {
  margin-bottom: 4px;
  font-size: 14px;
  color: #374151;
}

.solicitacao-actions {
  padding: 8px 16px;
  background: #f8fafc;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.action-btn {
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.observacao-field {
  margin: 0 16px 16px 16px;
}

.observacao-field :deep(.v-field) {
  background: #fffbeb;
  border-radius: 8px;
}

.nova-solicitacao-card {
  border-radius: 12px;
  border: 2px dashed #cbd5e1;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  min-height: 200px;
  background: rgba(var(--v-theme-primary), 0.02);
}

.nova-solicitacao-card:hover {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
  transform: translateY(-2px);
}

.nova-solicitacao-content {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.add-icon-container {
  text-align: center;
}

.add-text {
  font-size: 16px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin-top: 8px;
}

.solicitacao-form-container {
  min-height: 400px;
  padding: 16px;
}

/* Histórico */
.historico-card {
  height: 100%;
  border-radius: 16px;
}

.historico-header {
  padding: 16px 20px;
}

.historico-content {
  padding: 0;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.exame-item-card {
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: #f8fafc;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.exame-item-card:hover {
  background: #f1f5f9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* FAB */
.refresh-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

.refresh-fab:hover {
  background: rgb(var(--v-theme-secondary)) !important;
  transform: scale(1.05);
}

/* Skeleton loading */
.skeleton-item {
  margin: 8px 0;
  border-radius: 12px;
}

/* Responsividade */
@media (max-width: 1279px) {
  .solicitacoes-container .v-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (max-width: 959px) {
  .sidebar-col {
    margin-bottom: 16px;
  }
  
  .content-col {
    margin-bottom: 16px;
  }
  
  .solicitacoes-container .v-col {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

@media (max-width: 599px) {
  .selection-header {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .user-avatar {
    size: 44px;
  }
  
  .user-name {
    font-size: 1.125rem;
  }
  
  .sidebar-content {
    padding: 12px;
  }
  
  .content-body {
    padding: 16px;
  }
  
  .refresh-fab {
    bottom: 16px;
    right: 16px;
  }
}

/* Scrollbar personalizada */
.content-body::-webkit-scrollbar,
.historico-content::-webkit-scrollbar {
  width: 6px;
}

.content-body::-webkit-scrollbar-track,
.historico-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.content-body::-webkit-scrollbar-thumb,
.historico-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.content-body::-webkit-scrollbar-thumb:hover,
.historico-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}
</style>